import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
    /** Ícone à esquerda do rótulo (trigger e lista). */
    icon?: React.ReactNode;
}

/** Viewport abaixo do breakpoint `md` do Tailwind (768px). */
const MOBILE_DRAWER_MEDIA = '(max-width: 767px)';

function useMobileDrawerViewport(enabled: boolean) {
    const [matches, setMatches] = useState(() => {
        if (!enabled || typeof window === 'undefined') return false;
        return window.matchMedia(MOBILE_DRAWER_MEDIA).matches;
    });

    useEffect(() => {
        if (!enabled) return;
        const mq = window.matchMedia(MOBILE_DRAWER_MEDIA);
        const onChange = () => setMatches(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, [enabled]);

    return matches;
}

interface SelectProps {
    value: string;
    options: SelectOption[];
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    className?: string;
    selectClassName?: string;
    disabled?: boolean;
    /** Em telas estreitas (abaixo do breakpoint md do Tailwind), abre opções num painel fixo embaixo. */
    mobileBottomDrawer?: boolean;
    /** Título opcional no topo do painel mobile. */
    drawerTitle?: string;
}

export const Select: React.FC<SelectProps> = ({
    value,
    options,
    onChange,
    className = '',
    selectClassName = '',
    disabled = false,
    mobileBottomDrawer = false,
    drawerTitle,
}) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const drawerRootRef = useRef<HTMLDivElement>(null);
    const matchesMobileViewport = useMobileDrawerViewport(mobileBottomDrawer);
    const isMobileDrawer = mobileBottomDrawer && matchesMobileViewport;
    const selectedOption = useMemo(
        () => options.find((option) => option.value === value),
        [options, value]
    );

    useEffect(() => {
        if (!open || !isMobileDrawer) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open, isMobileDrawer]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (wrapperRef.current?.contains(target)) return;
            if (drawerRootRef.current?.contains(target)) return;
            setOpen(false);
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const handleOptionSelect = (nextValue: string) => {
        if (nextValue === value) {
            setOpen(false);
            return;
        }

        // Mantém compatibilidade com a assinatura atual de onChange.
        const syntheticEvent = {
            target: { value: nextValue },
        } as React.ChangeEvent<HTMLSelectElement>;

        onChange(syntheticEvent);
        setOpen(false);
    };

    const optionsList = (
        <ul role="listbox" className="space-y-1">
            {options.map((option) => {
                const isSelected = option.value === value;
                return (
                    <li key={option.value}>
                        <button
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            disabled={option.disabled}
                            onClick={() => handleOptionSelect(option.value)}
                            className={`flex w-full min-w-0 items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${option.disabled
                                ? 'cursor-not-allowed text-white/30'
                                : isSelected
                                    ? 'bg-orange-500/15 text-orange-400'
                                    : 'text-white/80 hover:bg-white/5 active:bg-white/10'
                                }`}
                        >
                            <span className={`flex min-w-0 flex-1 items-center gap-2 ${isMobileDrawer ? 'text-left' : ''}`}>
                                {option.icon ? (
                                    <span className="flex shrink-0 items-center" aria-hidden>
                                        {option.icon}
                                    </span>
                                ) : null}
                                <span className={isMobileDrawer ? 'min-w-0 break-words whitespace-normal' : 'whitespace-nowrap'}>
                                    {option.label}
                                </span>
                            </span>
                            {isSelected && <Check size={14} className="shrink-0" />}
                        </button>
                    </li>
                );
            })}
        </ul>
    );

    const drawer =
        open && !disabled && isMobileDrawer ? (
            <div
                ref={drawerRootRef}
                className="fixed inset-0 z-[200] flex flex-col justify-end md:hidden"
                role="presentation"
            >
                <button
                    type="button"
                    className="select-drawer-backdrop absolute inset-0 bg-black/70 backdrop-blur-[2px]"
                    aria-label="Fechar"
                    onClick={() => setOpen(false)}
                />
                <div className="select-drawer-sheet relative mt-auto max-h-[min(78vh,520px)] w-full rounded-t-2xl border border-white/10 border-b-0 bg-neutral-950 shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-white/20" aria-hidden />
                    <div className="flex items-center justify-between gap-3 border-b border-white/5 px-4 pb-3 pt-2">
                        <p className="min-w-0 truncate text-sm font-semibold text-white">
                            {drawerTitle ?? 'Opções'}
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="shrink-0 rounded-lg p-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
                            aria-label="Fechar"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div className="max-h-[min(60vh,420px)] overflow-y-auto overflow-x-hidden px-2 pb-2 pt-1">{optionsList}</div>
                </div>
            </div>
        ) : null;

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((prev) => !prev)}
                className={`flex w-full items-center justify-between gap-2 rounded-lg border border-white/10 px-4 py-2.5 text-left text-sm text-white/70 outline-none transition-colors hover:bg-white/10 focus:border-orange-500 disabled:cursor-not-allowed disabled:opacity-60 ${selectClassName}`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={`flex min-w-0 flex-1 items-center gap-2 ${selectedOption ? '' : 'text-white/40'}`}>
                    {selectedOption?.icon ? (
                        <span className="flex shrink-0 items-center" aria-hidden>
                            {selectedOption.icon}
                        </span>
                    ) : null}
                    <span className="truncate">{selectedOption?.label ?? 'Selecione'}</span>
                </span>
                <ChevronDown size={12} className={`shrink-0 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && !disabled && !isMobileDrawer && (
                <div className="absolute left-0 top-[calc(100%+0.35rem)] z-50 min-w-full w-max max-w-[calc(100vw-2rem)] max-h-64 overflow-y-auto overflow-x-auto rounded-xl border border-white/10 bg-black p-1 shadow-2xl">
                    {optionsList}
                </div>
            )}

            {typeof document !== 'undefined' && drawer ? createPortal(drawer, document.body) : null}
        </div>
    );
};
